import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { clone, take } from "lodash";
import { createPortal } from "react-dom";
import { Application } from "../applications/ApplicationsStore";
import { Modal, Button } from "../components/Modal";

interface ApplicationDesign {
    title: string;
    logo: string;
    subTitle: string;
}

const ApplicationMap: { [key: number]: Partial<ApplicationDesign> } = {
    0: {
        title: "",
        logo: "",
    },
    1: {
        title: "Portal",
        logo: "/images/applications/scadsys.png",
    },
    2: {
        title: "Scadsys",
        logo: "/images/applications/scadsys.png",
    },
    3: {
        title: "Forms",
        subTitle: "Complete required forms here",
        logo: "/images/applications/forms.png",
    },
    4: {
        subTitle: "Online Store",
        logo: "/images/applications/shop.png",
    },
    5: {
        title: "Screening",
        logo: "/images/applications/screening.png",
    },
};

const applicationDesign = (app: Application): ApplicationDesign => {
    let design = clone(ApplicationMap[app.applicationType] || {}) as ApplicationDesign;

    switch (app.name) {
        case "Scadsys educator portal":
            design.title = "Staff Portal";
            design.logo = "/images/applications/staff.png";
            break;
        case "Scadsys student portal":
            design.title = "Student Portal";
            design.logo = "/images/applications/student.png";
            break;
        case "Scadsys parent portal":
            design.title = "Parent Portal";
            design.logo = "/images/applications/parent.png";
            break;
        case "Scadsys admin portal":
            design.title = "Admin Portal";
            design.logo = "/images/applications/admin.png";
            break;
    }

    switch (app.applicationType) {
        case 4:
            design.title = app.name;
            break;
        default:
            // design.subTitle = app.name;
            break;
    }

    if (app.name == "RGB Gaming") {
        design.logo = "/images/applications/rgb.png";
    }

    return design;
};

let truncateText = (text: string, length: number) => {
    let parts = text.split(" ");
    let result = "";
    for (let i = 0; i < parts.length; i++) {
        if (result.length + parts[i].length > length) {
            break;
        } else {
            result += parts[i] + " ";
        }
    }

    if (result.length > length) {
        result = result.substring(0, length - 3);
    }

    if (text.length > length) {
        result += "...";
    }

    return result;
};

export let ApplicationCard = ({ app }: { app: Application }) => {
    let design = applicationDesign(app);
    
    const [showIframeModal, setShowIframeModal] = React.useState(false);
    const shouldOpenInIframe = app.applicationType === 0 && app.configuration?.openInIframe;

    const handleLaunchClick = (e: React.MouseEvent) => {
        if (shouldOpenInIframe) {
            e.preventDefault();
            setShowIframeModal(true);
        }
        // Otherwise, let the default link behavior handle it
    };

    return (
        <div className="group relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gray-100/50 backdrop-blur-sm">
            {/* Background Pattern */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 rounded-full -translate-y-8 translate-x-8 transition-transform duration-500 group-hover:scale-110"></div>

            {/* Card Header */}
            <div className="relative flex items-start p-6 pb-4">
                <div className="flex-shrink-0 mr-4">
                    <div className="relative w-14 h-14 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-2xl flex items-center justify-center overflow-hidden shadow-sm border border-blue-100/30 group-hover:shadow-lg transition-all duration-300">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent rounded-2xl"></div>
                        <img src={design.logo || app.logo} alt={design.title || "Application"} className="relative z-10 w-10 h-10 object-contain drop-shadow-sm" />
                    </div>
                </div>
                <div className="flex-1 min-w-0 pt-1">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 leading-tight">{design.title || app.name}</h3>
                    {design.subTitle && <p className="text-sm text-gray-500 mt-1 leading-relaxed line-clamp-2">{design.subTitle}</p>}
                </div>
            </div>

            {/* Status Indicator */}
            <div className="absolute top-6 right-6 w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full shadow-sm animate-pulse"></div>

            {/* Card Content Spacer */}
            <div className="px-6 flex-1">
                {/* Subtle divider */}
                <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-4 opacity-60"></div>
            </div>

            {/* Card Actions */}
            <div className="relative px-6 pb-6 pt-2">
                <div className="flex flex-wrap gap-2">
                    {/* Secondary Actions */}
                    {take(app.actions, 2).map((a, i) => (
                        <a
                            href={a.url}
                            key={i}
                            className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg no-underline"
                        >
                            <span>{truncateText(a.text, 12)}</span>
                            <FontAwesomeIcon size="xs" icon={["fad", "external-link"]} className="opacity-70" />
                        </a>
                    ))}

                    {/* Primary Action */}
                    {app.url && (
                        <a
                            href={shouldOpenInIframe ? "#" : app.url}
                            onClick={handleLaunchClick}
                            className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg no-underline"
                        >
                            <span>Launch</span>
                            <FontAwesomeIcon size="xs" icon={["fad", shouldOpenInIframe ? "window-restore" : "rocket"]} className="text-white/90" />
                        </a>
                    )}
                </div>
            </div>

            {/* Enhanced Hover Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/3 via-transparent to-indigo-500/3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

            {/* Shine Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-full group-hover:-translate-x-full transition-transform duration-1000 pointer-events-none"></div>
        
            {/* Iframe Modal - Rendered as Portal outside card container */}
            {shouldOpenInIframe && showIframeModal && createPortal(
                <Modal 
                    isOpen={showIframeModal} 
                    onClose={() => setShowIframeModal(false)}
                    title={design.title || app.name}
                    icon={["fad", "window-restore"]}
                    iconColor="text-blue-600"
                    size="full"
                >
                    <div className="flex-1">
                        <div className="w-full h-full min-h-[600px] bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                            <iframe 
                                src={app.url}
                                className="w-full h-full min-h-[600px] border-0"
                                title={design.title || app.name}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-modals"
                            />
                        </div>
                    </div>
                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center text-sm text-gray-500">
                            <FontAwesomeIcon icon={["fad", "info-circle"]} className="mr-2" />
                            <span>Embedded content from: {new URL(app.url).hostname}</span>
                        </div>
                        <div className="flex space-x-3">
                            <Button variant="secondary" onClick={() => window.open(app.url, '_blank')}>
                                <FontAwesomeIcon icon={["fad", "external-link"]} className="mr-2" />
                                Open in New Tab
                            </Button>
                            <Button variant="primary" onClick={() => setShowIframeModal(false)}>
                                Close
                            </Button>
                        </div>
                    </div>
                </Modal>,
                document.body
            )}
        </div>
    );
};
